import { type OntologyMatchingLlmPatch } from "./application-ontology-matching";
import {
  type FigmaApiFileResponse,
  type FigmaApiNodesResponse,
  buildFigmaTemplateBenchmarkCasesFromApiResponse,
  fetchFigmaTemplateBenchmarkCaseFromFigmaApi,
  type FigmaTemplateFetchLike,
  type FigmaTemplateSourceDescriptor,
} from "./figma-template-live-ingest";
import {
  buildFigmaTemplateOntologyMappingProof,
  type FigmaTemplateOntologyMappingReport,
  type FigmaTemplateOntologyMappingStatus,
} from "./figma-template-ontology-mapping";
import {
  type FigmaTemplateBenchmarkCase,
  type FigmaTemplateBenchmarkTier,
  type FigmaTemplatePlatform,
} from "./figma-template-ontology-benchmark";

export const FIGMA_TEMPLATE_LIVE_SOURCE_MAPPING_SCHEMA_VERSION =
  "cogo.figma-template-live-source-mapping.v1";

export type FigmaTemplateLiveSourceMode =
  | "recorded-api-response"
  | "live-fetch";

export type FigmaTemplateLiveSourceMappingStatus =
  | FigmaTemplateOntologyMappingStatus
  | "blocked";

export interface FigmaTemplateLiveSourceInput extends FigmaTemplateSourceDescriptor {
  response?: FigmaApiNodesResponse | FigmaApiFileResponse;
  accessToken?: string;
  apiBaseUrl?: string;
  fetcher?: FigmaTemplateFetchLike;
}

export interface FigmaTemplateLiveSourceEvidence {
  sourceId: string;
  title: string;
  tier: FigmaTemplateBenchmarkTier;
  platform: FigmaTemplatePlatform;
  sourceMode?: FigmaTemplateLiveSourceMode;
  admitted: boolean;
  caseIds: string[];
  templateIds: string[];
  designSystemIds: string[];
  blockedReasons: string[];
}

export interface FigmaTemplateLiveSourceMappingReport {
  schemaVersion: typeof FIGMA_TEMPLATE_LIVE_SOURCE_MAPPING_SCHEMA_VERSION;
  status: FigmaTemplateLiveSourceMappingStatus;
  sourceCount: number;
  admittedSourceCount: number;
  blockedSourceCount: number;
  admittedCaseCount: number;
  sourceEvidence: FigmaTemplateLiveSourceEvidence[];
  mappingReport: FigmaTemplateOntologyMappingReport;
}

export interface BuildFigmaTemplateLiveSourceMappingProofInput {
  sources: readonly FigmaTemplateLiveSourceInput[];
  llmPatchByCaseId?: Readonly<Record<string, OntologyMatchingLlmPatch>>;
}

export async function buildFigmaTemplateLiveSourceMappingProof(
  input: BuildFigmaTemplateLiveSourceMappingProofInput,
): Promise<FigmaTemplateLiveSourceMappingReport> {
  const admittedCases: FigmaTemplateBenchmarkCase[] = [];
  const sourceEvidence: FigmaTemplateLiveSourceEvidence[] = [];

  for (const source of input.sources) {
    const sourceId =
      source.id ?? source.fileKey ?? source.url ?? "figma-source";
    try {
      const { mode, cases } = await resolveSourceCases(source);
      admittedCases.push(...cases);
      sourceEvidence.push({
        sourceId,
        title: source.title ?? sourceId,
        tier: source.tier,
        platform: source.platform,
        sourceMode: mode,
        admitted: true,
        caseIds: cases.map((entry) => entry.id),
        templateIds: cases.map((entry) => entry.templateId),
        designSystemIds: cases.map((entry) => entry.designSystemId),
        blockedReasons: [],
      });
    } catch (error) {
      sourceEvidence.push({
        sourceId,
        title: source.title ?? sourceId,
        tier: source.tier,
        platform: source.platform,
        admitted: false,
        caseIds: [],
        templateIds: [],
        designSystemIds: [],
        blockedReasons: [
          error instanceof Error ? error.message : String(error),
        ],
      });
    }
  }

  const mappingReport = buildFigmaTemplateOntologyMappingProof(admittedCases, {
    llmPatchByCaseId: input.llmPatchByCaseId,
  });
  const blockedSourceCount = sourceEvidence.filter(
    (entry) => !entry.admitted,
  ).length;
  const status =
    sourceEvidence.length === 0 || admittedCases.length === 0
      ? "blocked"
      : blockedSourceCount > 0
        ? "fail"
        : mappingReport.status;

  return {
    schemaVersion: FIGMA_TEMPLATE_LIVE_SOURCE_MAPPING_SCHEMA_VERSION,
    status,
    sourceCount: sourceEvidence.length,
    admittedSourceCount: sourceEvidence.length - blockedSourceCount,
    blockedSourceCount,
    admittedCaseCount: admittedCases.length,
    sourceEvidence,
    mappingReport,
  };
}

async function resolveSourceCases(
  source: FigmaTemplateLiveSourceInput,
): Promise<{
  mode: FigmaTemplateLiveSourceMode;
  cases: FigmaTemplateBenchmarkCase[];
}> {
  if (source.response) {
    return {
      mode: "recorded-api-response",
      cases: buildFigmaTemplateBenchmarkCasesFromApiResponse({
        source,
        response: source.response,
      }),
    };
  }
  if (!source.fetcher && !source.accessToken) {
    throw new Error(
      "Figma live source requires a recorded API response, injected fetcher, or access token; URL-only sources are not admitted ontology.",
    );
  }
  const benchmarkCase =
    await fetchFigmaTemplateBenchmarkCaseFromFigmaApi(source);
  return {
    mode: "live-fetch",
    cases: [benchmarkCase],
  };
}
